import { api } from '@setplex/tria-api'
import { combine, createEvent, sample } from 'effector'
import { not, or } from 'patronum'
import { model as router } from '~/core/router'
import { liveEventsPurchasedEntityModel } from '~/entities/live-events/purchased'
import { moviesPurchasedEntityModel } from '~/entities/movies/purchased'
import { showsPurchasedEntityModel } from '~/entities/shows/purchased'
import { tvPurchasedEntityModel } from '~/entities/tv/purchased'
import { profilesFeaturesModel } from '~/features/profiles'
import {
  PATH_LIBRARY_PURCHASED_EVENTS,
  PATH_LIBRARY_PURCHASED_MOVIES,
  PATH_LIBRARY_PURCHASED_SERIES,
  PATH_LIBRARY_PURCHASED_TV,
} from '~/shared/constants/libraryRoutes'
import { aye } from '~/shared/helpers'

export const loadPurchased = createEvent()
export const resetPurchased = createEvent()

const handleLoadPurchased = createEvent()

sample({
  clock: loadPurchased,
  source: profilesFeaturesModel.$isActiveProfileRegular,
  filter: aye,
  target: handleLoadPurchased,
})

sample({
  clock: handleLoadPurchased,
  filter: not(api.tv.getPurchasedTvChannelsRowFx.pending),
  target: api.tv.getPurchasedTvChannelsRowFx,
})

sample({
  clock: handleLoadPurchased,
  filter: not(api.vods.getPurchasedVodsRowFx.pending),
  target: api.vods.getPurchasedVodsRowFx,
})

sample({
  clock: handleLoadPurchased,
  filter: not(api.tvshows.getPurchasedTvShowsRowFx.pending),
  target: api.tvshows.getPurchasedTvShowsRowFx,
})

sample({
  clock: handleLoadPurchased,
  filter: not(api.liveEvents.getPurchasedLiveEventsRowFx.pending),
  target: api.liveEvents.getPurchasedLiveEventsRowFx,
})

sample({
  clock: resetPurchased,
  target: [
    tvPurchasedEntityModel.resetPurchasedTvChannelsRow,
    moviesPurchasedEntityModel.resetPurchasedVodsRow,
    showsPurchasedEntityModel.resetPurchasedTvShowsRow,
    liveEventsPurchasedEntityModel.resetPurchasedLiveEventsRow,
  ],
})

export const $isAnyPurchasedFxPending = or(
  api.vods.getPurchasedVodsRowFx.pending,
  api.tv.getPurchasedTvChannelsRowFx.pending,
  api.tvshows.getPurchasedTvShowsRowFx.pending,
  api.liveEvents.getPurchasedLiveEventsRowFx.pending
)

export const $purchasedContent = combine({
  tvChannels: tvPurchasedEntityModel.$purchasedTvChannelsRowFormatted,
  vods: moviesPurchasedEntityModel.$purchasedVodsRowFormatted,
  tvShows: showsPurchasedEntityModel.$purchasedTvShowsRowFormatted,
  liveEvents: liveEventsPurchasedEntityModel.$purchasedLiveEventsRowFormatted,
})

export const $isPurchasedContentInited = combine(
  {
    tvChannels: tvPurchasedEntityModel.$purchasedTvChannelsRow,
    vods: moviesPurchasedEntityModel.$purchasedVodsRow,
    tvShows: showsPurchasedEntityModel.$purchasedTvShowsRow,
    liveEvents: liveEventsPurchasedEntityModel.$purchasedLiveEventsRow,
  },
  ({ tvChannels, vods, tvShows, liveEvents }) =>
    Boolean(tvChannels && vods && tvShows && liveEvents)
)

export const $isAnyPurchased = combine(
  {
    tvChannels: tvPurchasedEntityModel.$purchasedTvChannelsRow,
    vods: moviesPurchasedEntityModel.$purchasedVodsRow,
    tvShows: showsPurchasedEntityModel.$purchasedTvShowsRow,
    liveEvents: liveEventsPurchasedEntityModel.$purchasedLiveEventsRow,
  },
  ({ tvChannels, vods, tvShows, liveEvents }) =>
    Boolean(
      tvChannels?.content.length ||
      vods?.content.length ||
      tvShows?.content.length ||
      liveEvents?.content.length
    )
)

export const seeAllLibraryPurchasedTvClick = createEvent()
export const seeAllLibraryPurchasedMovieClick = createEvent()
export const seeAllLibraryPurchasedTvShowsClick = createEvent()
export const seeAllLibraryPurchasedLiveEventsClick = createEvent()

sample({
  clock: seeAllLibraryPurchasedTvClick,
  fn: () => PATH_LIBRARY_PURCHASED_TV,
  target: router.navigatePush,
})

sample({
  clock: seeAllLibraryPurchasedMovieClick,
  fn: () => PATH_LIBRARY_PURCHASED_MOVIES,
  target: router.navigatePush,
})

sample({
  clock: seeAllLibraryPurchasedTvShowsClick,
  fn: () => PATH_LIBRARY_PURCHASED_SERIES,
  target: router.navigatePush,
})

sample({
  clock: seeAllLibraryPurchasedLiveEventsClick,
  fn: () => PATH_LIBRARY_PURCHASED_EVENTS,
  target: router.navigatePush,
})

sample({
  clock: profilesFeaturesModel.$selectedProfileId,
  filter: aye,
  target: [
    tvPurchasedEntityModel.$purchasedTvChannelsRow.reinit,
    moviesPurchasedEntityModel.$purchasedVodsRow.reinit,
    showsPurchasedEntityModel.$purchasedTvShowsRow.reinit,
    liveEventsPurchasedEntityModel.$purchasedLiveEventsRow.reinit,
  ],
})
